/* eslint-disable @typescript-eslint/no-explicit-any */
import IconArrow from '@assets/icons/big arrow right.svg?react';
import {
 Box,
 Chip,
 List,
 ListItem,
 ListItemText,
 Tooltip,
 Typography,
 useTheme,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';

import customColors from '../../../utils/customColors';
import { isJson } from '../../../utils/dataTypes';
import { usePage } from '../../../contexts/usePageContext';
import { useTranslation } from 'react-i18next';
import UserAvatar from '../Header/UserAvatar';
import { getConfigStorageItem } from '@/hooks/useConfigStorage';

const limitColorMap = {
 red: customColors.lightRed,
 green: customColors.lightGreen,
 orange: customColors.lightOrange,
 yellow: customColors.lightYellow,
 blue: customColors.lightBlue,
};

const statusMap = {
 0: customColors.lightGreen,
 1: customColors.lightYellow,
 2: customColors.lightYellow,
 3: customColors.lightRed,
 4: customColors.lightBlue,
};
const statusMapDark = {
 0: customColors.green,
 1: customColors.yellow,
 2: customColors.yellow,
 3: customColors.red,
 4: customColors.blue,
};

// Each Orders chip column owns a disjoint hue family so colors never repeat
// across Type / Status / WebStatus. Within a column each enum value gets its
// own shade of that family.
//
// WebStatus (0-8) -> purple / violet family.
const webStatusMap = {
 0: '#F1E5FF',
 1: '#E7D4FF',
 2: '#DDC3FF',
 3: '#D3B2FF',
 4: '#C9A1FF',
 5: '#BF90FF',
 6: '#B47FFA',
 7: '#A96EF0',
 8: '#9E5DE6',
};
const webStatusMapDark = {
 0: '#9B6BFF',
 1: '#8E59F5',
 2: '#8147EB',
 3: '#7A3FE0',
 4: '#7338D6',
 5: '#6B30CC',
 6: '#6429C2',
 7: '#5D22B8',
 8: '#561BAE',
};

// Status (0-6) -> teal / green family.
const orderStatusMap = {
 0: '#D9FBE7',
 1: '#BFF4DA',
 2: '#A6EDCE',
 3: '#8DE6C5',
 4: '#74DEC0',
 5: '#58D2B8',
 6: '#3FC9B0',
};
const orderStatusMapDark = {
 0: '#10B981',
 1: '#0FB39A',
 2: '#0FAEA4',
 3: '#0EA5A5',
 4: '#0D9C9C',
 5: '#0C8E92',
 6: '#0B8089',
};

// Type (0-2) -> blue / indigo family.
const orderTypeMap = {
 0: '#DCE6FF',
 1: '#B6CCFF',
 2: '#8FB0FF',
};
const orderTypeMapDark = {
 0: '#5B7BFF',
 1: '#4666E5',
 2: '#2E4ECC',
};

// Deterministic chip colors for free-form enum fields (e.g. IoT DeviceType /
// IotSource.ServerType) where the value set isn't known ahead of time.
const typeColorPalette = [
 customColors.lightBlue,
 customColors.lightGreen,
 customColors.lightYellow,
 customColors.lightOrange,
 customColors.lightRed,
];
const typeColorPaletteDark = [
 customColors.blue,
 customColors.green,
 customColors.yellow,
 customColors.orange,
 customColors.red,
];
const isTypeColorField = (field: string) =>
 field === 'DeviceType' ||
 field === 'ServerType' ||
 field === 'IotType' ||
 field.endsWith('.DeviceType') ||
 field.endsWith('.ServerType') ||
 field.endsWith('.IotType');
const typeColorIndex = (key: unknown) => {
 const s = String(key ?? '');
 let h = 0;
 for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
 return h % typeColorPalette.length;
};

const xRenderCell = (
 params: GridRenderCellParams,
 name: string,
 type: string,
 customValue: string = '',
) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const th = useTheme();
 const value =
  customValue && customValue !== 'undefined' ? customValue : params.value;
 let output: any = value;

 if (params?.rowNode?.type === 'group') {
  output = params?.formattedValue ? params.rowNode.groupingKey : value;
 } else if (type === 'boolean') {
  output = (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? theme.palette.kemek.white // not actually white
       : theme.palette.kemek.darkestGray,
     backgroundColor: (statusMap as any)?.[value ? 0 : 3],
    }}
   >
    {value ? t('global.yes') : t('global.no')}
   </Typography>
  );
 } else if ((!value && value !== 0) || value === 'null') {
  output = '';
 } else if (
  params.field === 'ProfileImageBase64' &&
  params.row.ProfileImageBase64
 ) {
  output = (
   <Box
    sx={{
     width: '36px',
     height: '36px',
     backgroundImage: `url(data:image/png;base64,${params.row.ProfileImageBase64})`,
     backgroundSize: 'cover',
     backgroundPosition: 'center',
     backgroundRepeat: 'no-repeat',
     borderRadius: '50%',
     display: 'flex',
    }}
   />
  );
 } else if (
  params.field === 'State' ||
  (pageName === 'orders' &&
   ['Status', 'WebStatus', 'Type'].includes(params.field))
 ) {
  output = (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? '#fff' // not actually white
       : theme.palette.kemek.darkestGray,
     backgroundColor: (theme) => {
      let lightMap: any = statusMap;
      let darkMap: any = statusMapDark;
      if (pageName === 'orders') {
       if (params.field === 'WebStatus') {
        lightMap = webStatusMap;
        darkMap = webStatusMapDark;
       } else if (params.field === 'Status') {
        lightMap = orderStatusMap;
        darkMap = orderStatusMapDark;
       } else if (params.field === 'Type') {
        lightMap = orderTypeMap;
        darkMap = orderTypeMapDark;
       }
      }
      return (theme.palette.mode === 'dark' ? darkMap : lightMap)?.[
       params.value
      ];
     },
    }}
   >
    {value}
   </Typography>
  );
 } else if (isTypeColorField(params.field)) {
  const idx = typeColorIndex(params.value ?? value);
  output = (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? '#fff'
       : theme.palette.kemek.darkestGray,
     backgroundColor: (theme) =>
      (theme.palette.mode === 'dark'
       ? typeColorPaletteDark
       : typeColorPalette)[idx],
    }}
   >
    {value}
   </Typography>
  );
 } else if (params.field === 'Cargo') {
  output = (
   <Tooltip title={value}>
    <Box
     sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      background: (theme) =>
       params.formattedValue === 1
        ? theme.palette.kemek.darkestGray
        : theme.palette.kemek.lightBlue,
      width: '24px',
      height: '24px',
     }}
    >
     <IconArrow
      title={value}
      style={{
       color:
        params.formattedValue === 1
         ? th.palette.kemek.white
         : th.palette.kemek.darkestGray,
       transform: params.formattedValue === 1 ? 'rotate(180deg)' : '',
      }}
     />
    </Box>
   </Tooltip>
  );
 } else if (type === 'datetime') {
  if (value === null || value === undefined || value === '') {
   output = '';
  } else if (Array.isArray(value)) {
   if (!value.length) {
    output = '';
   } else {
    const formattedDates = value
     .map((item: any) => {
      if (item === null || item === undefined || item === '') {
       return '';
      }

      const dateValue = dayjs(item);
      return dateValue.isValid() ? dateValue.format('YYYY-MM-DD HH:mm:ss') : '';
     })
     .filter(Boolean);

    output = formattedDates.join(', ');
   }
  } else {
   const dateValue = dayjs(value);
   output = dateValue.isValid() ? dateValue.format('YYYY-MM-DD HH:mm:ss') : '';
  }
 } else if (type === 'array' || typeof value === 'object') {
  const keysPriority = [
   'Name',
   'FullName',
   'Weight',
   'Number',
   'Time',
   'Client',
   'ValidFrom', //destPoint.Details
  ];

  let arrayReturn = [];
  if (['ResultProducts', 'CardProducts'].includes(params.field)) {
   const hasMultipleProducts = value.length > 1;
   arrayReturn = value.map((item: any) => {
    let returnValue = `${item.Product.Name}`;
    const percentageValue = Number(item.Percentage);
    const hasPercentage =
     Number.isFinite(percentageValue) && percentageValue > 0;
    const shouldShowPercentage = hasMultipleProducts || percentageValue !== 100;
    if (hasPercentage && shouldShowPercentage) {
     returnValue += ` (${item.Percentage}%)`;
    }
    return returnValue;
   });
  } else if (value[0] && typeof value[0] !== 'object') {
   arrayReturn = value;
  } else {
   if (value[0] === null) {
    arrayReturn = [];
   } else if (value[0] && keysPriority.some((key) => key in value[0])) {
    const keyFound = keysPriority.find((key) => key in value[0]) as string;
    arrayReturn = value.map((item: any) => item[keyFound as keyof typeof item]);
   } else {
    arrayReturn = Object.keys(value).length ? value : [];
   }
  }

  if (arrayReturn.length <= 1) {
   output = <>{arrayReturn.join(', ')}</>;
  } else if (arrayReturn.length > 1) {
   output = (
    <Tooltip
     title={
      <Box sx={{ p: 0 }}>
       {arrayReturn.map((item: any, index: number) => (
        <Box key={`${item}-${index}`}>{item}</Box>
       ))}
      </Box>
     }
    >
     <Chip
      //  color="primary"
      sx={{ fontWeight: 500 }}
      variant="outlined"
      label={`${arrayReturn[0]} +${arrayReturn.length - 1}`}
      size="small"
     />
    </Tooltip>
   );
  } else if (value[0] && keysPriority.find((key) => key in value[0])) {
   // this shouldnt happen
   const keyFound = keysPriority.find((key) => key in value[0]) as string;
   output = arrayReturn[keyFound as keyof typeof arrayReturn];
  } else {
   output = JSON.stringify(arrayReturn);
  }
 } else if (['int32', 'int16', 'double', 'decimal'].includes(type)) {
  // rewrite fix
  const lsValue = getConfigStorageItem(pageName);
  const tempState = lsValue?.columns || [];
  const thisLimits = tempState?.limit?.[params.field] || [];
  let lastLimitIndex = -1;
  if (thisLimits.length) {
   thisLimits.forEach((item: any) => {
    if (item.limit === '<') {
     if (value < item.value) {
      lastLimitIndex = thisLimits.indexOf(item);
     }
    } else if (item.limit === '>') {
     if (value > item.value) {
      lastLimitIndex = thisLimits.indexOf(item);
     }
    }
   });
  }

  const selectedValue = value || params.value;
  const parts = selectedValue.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const formattedValue = parts.join('.');
  const hasLimit = lastLimitIndex !== -1;

  output =
   !hasLimit || params.aggregation ? (
    formattedValue
   ) : (
    <Typography
     className="badge-item"
     sx={{
      color: (theme) =>
       theme.palette.mode === 'dark'
        ? theme.palette.kemek.white // not actually white
        : theme.palette.kemek.darkestGray,
      backgroundColor: (limitColorMap as any)?.[
       hasLimit ? thisLimits[lastLimitIndex].color : 'blue'
      ],
     }}
    >
     {formattedValue}
    </Typography>
   );
 } else {
  output = value;
 }

 return (
  <Box
   sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
  >
   {output}
  </Box>
 );
};

export default xRenderCell;
